import { Facebook, Instagram, Twitter, MapPin, Phone, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useNewsletter } from '@/hooks/useNewsletter';
import { useCityNavigation } from '@/hooks/useCityNavigation';
import { useDeliveryLocation } from '@/contexts/DeliveryLocationContext';
import { egyptianLocations } from '@/data/locations';
import { useAreas } from '@/hooks/useAreas';

interface FooterProps {
  language: string;
}

const Footer = ({ language }: FooterProps) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { subscribeToNewsletter } = useNewsletter();
  const { buildCityPath } = useCityNavigation();
  const { selectedCity } = useDeliveryLocation();

  // Determine which city to use
  const city = selectedCity || egyptianLocations.find(c => c.slug === 'cairo');
  
  // Fetch areas from database for the selected city
  const { data: dbAreas = [] } = useAreas(city?.slug);
  const areas = dbAreas.slice(0, 5);
  
  // Helper for area name
  const getAreaName = (area: any) => {
    if (language === 'ar') {
      return area.name_ar || area.name_en || area.name;
    }
    return area.name_en || area.name_ar || area.name;
  };

  const translations = {
    en: {
      tagline: "Spreading joy through beautiful flowers",
      quickLinks: "Quick Links",
      aboutUs: "About Us",
      flowers: "Flowers",
      gifts: "Gifts", 
      occasions: "Occasions",
      contact: "Contact Us",
      faq: "FAQ",
      shipping: "Shipping Info",
      returns: "Returns",
      customerService: "Customer Service",
      phone: "+201091565658",
      email: "hello@bouqueh.com",
      address: "123 Tahrir Square, Cairo, Egypt",
      newsletter: "Newsletter",
      newsletterText: "Subscribe to get special offers and updates",
      subscribe: "Subscribe",
      followUs: "Follow Us",
      copyright: "© 2024 Bouqueh. All rights reserved.",
      privacy: "Privacy Policy",
      terms: "Terms of Service"
    },
    ar: {
      tagline: "نشر البهجة من خلال الورد الجميلة",
      quickLinks: "روابط سريعة",
      aboutUs: "معلومات عنا",
      flowers: "الورد",
      gifts: "الهدايا",
      occasions: "المناسبات",
      contact: "اتصل بنا",
      faq: "الأسئلة الشائعة",
      shipping: "معلومات الشحن",
      returns: "المرتجعات",
      customerService: "خدمة العملاء",
      phone: "+201091565658",
      email: "hello@bouqueh.com",
      address: "123 ميدان التحرير، القاهرة، مصر",
      newsletter: "النشرة الإخبارية",
      newsletterText: "اشترك للحصول على عروض خاصة وتحديثات",
      subscribe: "اشترك",
      followUs: "تابعنا",
      copyright: "© 2024 بوكيه. جميع الحقوق محفوظة.",
      privacy: "سياسة الخصوصية",
      terms: "شروط الخدمة"
    }
  };

  const t = translations[language as keyof typeof translations];

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) return;
    
    setIsSubmitting(true);
    try {
      await subscribeToNewsletter(email.trim(), 'footer');
      setEmail(''); // Clear the input on success
    } catch (error) {
      console.error('Newsletter signup error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-muted text-foreground">
      <div className="container mx-auto px-4 py-12">
        {/* 6 columns on lg so Cities + Popular Areas + Newsletter all sit side-by-side.
            md: 2 cols (wraps cleanly), mobile: stacked. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Brand */}
          <div className={`flex-1 space-y-4 ${language === 'ar' ? 'text-right' : ''}`}>
            <div className="flex items-center">
              <h3 className="text-2xl font-bold text-foreground">
                Bouqueh
              </h3>
              <span className="ml-2 text-warning">🌹</span>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              {t.tagline}
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/bouqueh.official" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-muted-foreground hover:text-warning transition-colors"
                aria-label={language === 'ar' ? 'تابعنا على فيسبوك' : 'Follow us on Facebook'}
              >
                <Facebook className="h-5 w-5" aria-hidden="true" />
              </a>
              <a 
                href="https://www.instagram.com/bouqueh.official" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-muted-foreground hover:text-warning transition-colors"
                aria-label={language === 'ar' ? 'تابعنا على إنستغرام' : 'Follow us on Instagram'}
              >
                <Instagram className="h-5 w-5" aria-hidden="true" />
              </a>
              <a 
                href="https://twitter.com/BouquehOfficial" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-muted-foreground hover:text-warning transition-colors"
                aria-label={language === 'ar' ? 'تابعنا على تويتر' : 'Follow us on Twitter'}
              >
                <Twitter className="h-5 w-5" aria-hidden="true" />
              </a>
              <a 
                href="https://www.youtube.com/channel/UCl6SSJjQg6YuEEVuHsOo_rQ" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-muted-foreground hover:text-warning transition-colors"
                aria-label={language === 'ar' ? 'تابعنا على يوتيوب' : 'Follow us on YouTube'}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M23.498 6.186a2.994 2.994 0 0 0-2.108-2.116C19.228 3.5 12 3.5 12 3.5s-7.228 0-9.39.57A2.994 2.994 0 0 0 .502 6.186C0 8.36 0 12 0 12s0 3.64.502 5.814a2.994 2.994 0 0 0 2.108 2.116C4.772 20.5 12 20.5 12 20.5s7.228 0 9.39-.57a2.994 2.994 0 0 0 2.108-2.116C24 15.64 24 12 24 12s0-3.64-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className={`flex-1 space-y-4 ${language === 'ar' ? 'text-right' : ''}`}>
            <h4 className="text-lg font-semibold text-foreground">{t.quickLinks}</h4>
            <ul className="space-y-2">
              <li><Link to={buildCityPath('/about')} className="text-foreground hover:text-primary transition-colors">{t.aboutUs}</Link></li>
              <li><Link to={buildCityPath('/flowers')} className="text-muted-foreground hover:text-warning transition-colors">{t.flowers}</Link></li>
              <li><Link to={buildCityPath('/category')} className="text-muted-foreground hover:text-warning transition-colors">{t.gifts}</Link></li>
              <li><Link to={buildCityPath('/occasion')} className="text-muted-foreground hover:text-warning transition-colors">{t.occasions}</Link></li>
              <li><Link to={buildCityPath('/faq')} className="text-muted-foreground hover:text-warning transition-colors">{t.faq}</Link></li>
            </ul>
          </div>

          {/* Customer Service */}
          <div className={`flex-1 space-y-4 ${language === 'ar' ? 'text-right' : ''}`}>
            <h4 className="text-lg font-semibold text-foreground">{t.customerService}</h4>
            <ul className="space-y-2">
              <li><Link to="/contact" className="text-foreground hover:text-primary transition-colors">{t.contact}</Link></li>
              <li><Link to="/shipping" className="text-muted-foreground hover:text-warning transition-colors">{t.shipping}</Link></li>
              <li><Link to="/returns" className="text-muted-foreground hover:text-warning transition-colors">{t.returns}</Link></li>
            </ul>
            {/* Removed contact info (phone, email, address) */}
          </div>

          {/* Cities We Deliver To — own column. Always rendered (bot-safe static list). */}
          <div className={`flex-1 space-y-4 ${language === 'ar' ? 'text-right' : ''}`}>
            <h4 className="text-lg font-semibold text-foreground">{language === 'ar' ? 'مدن التوصيل' : 'Cities We Deliver To'}</h4>
            <ul className="space-y-2">
              {egyptianLocations.slice(0, 8).map(c => (
                <li key={c.slug}>
                  <Link
                    to={`/${language === 'ar' ? 'ar-eg' : 'en-eg'}/${c.slug}`}
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    {language === 'ar' ? (c.name_ar || c.name_en || c.name) : (c.name_en || c.name)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Areas in <current city> — separate column next to Cities.
              Only rendered when the DB areas query has resolved with results.
              Mobile: stacks below cities; desktop: sits beside it. */}
          {areas.length > 0 && (
            <div className={`flex-1 space-y-4 ${language === 'ar' ? 'text-right' : ''}`}>
              <h4 className="text-lg font-semibold text-foreground">
                {language === 'ar'
                  ? `أحياء ${getAreaName(city as unknown as { name_en?: string; name_ar?: string; name?: string }) || ''}`
                  : `Popular Areas in ${city?.name_en || city?.name || ''}`}
              </h4>
              <ul className="space-y-2">
                {areas.map(area => (
                  <li key={area.slug}>
                    <Link
                      to={`/${language === 'ar' ? 'ar-eg' : 'en-eg'}/area/${city?.slug}/${area.slug}`}
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      {getAreaName(area)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Newsletter */}
          <div className={`flex-1 space-y-4 ${language === 'ar' ? 'text-right' : ''}`}>
            <h4 className="text-lg font-semibold text-foreground">{t.newsletter}</h4>
            <p className="text-muted-foreground">{t.newsletterText}</p>
            <form className="flex flex-col md:flex-row items-stretch gap-2 mt-2">
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
              <button type="submit" className="w-full md:w-auto px-4 py-2 rounded-md bg-primary text-white font-semibold hover:bg-primary/90 transition-colors" disabled={isSubmitting}>
                {isSubmitting ? 'Subscribing...' : t.subscribe}
              </button>
            </form>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-border mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-muted-foreground text-sm">{t.copyright}</p>
          <div className={`flex items-center mt-4 md:mt-0 ${language === 'ar' ? 'space-x-reverse space-x-0 space-x-6 rtl:space-x-reverse rtl:space-x-0 rtl:space-x-6 gap-6' : 'space-x-6'}`}> 
            <Link to={buildCityPath('/privacy')} className="text-foreground hover:text-primary text-sm transition-colors ml-3">{t.privacy}</Link>
            <Link to={buildCityPath('/terms')} className="text-foreground hover:text-primary text-sm transition-colors ml-3">{t.terms}</Link>
            <img 
              src="/payment/visa.webp" 
              alt="Visa" 
              className="h-6 inline-block ml-3" 
              width="24"
              height="24"
              style={{ aspectRatio: '1/1' }}
            />
            <img 
              src="/payment/mastercard.webp" 
              alt="Mastercard" 
              className="h-6 inline-block ml-3" 
              width="24"
              height="24"
              style={{ aspectRatio: '1/1' }}
            />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
